import { memo } from "react"

import { cn } from "@productlane/lib/src/cn"
import { Loader, Select, Switch } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { analytics } from "@/lib/analytics"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { MultipleCustomersCard } from "../../settings/multiple-customers-card"
import { PortalSettingsCard } from "../../settings/portal-settings-card"
import { PublishPortalCard } from "../../settings/publish-portal-card"
import { SettingItem } from "../../settings/settings-switches/setting-item"
import { SettingsContainer } from "../../settings/settings-switches/settings-container"
import { TeamRequestsSwitch } from "../../settings/support-portal-settings-content"
import { ROADMAP_SORT_OPTIONS } from "./roadmap-sort-options"

export const RoadmapSettings = memo(() => {
	const zero = useZero()
	const workspace = useZeroWorkspace()

	if (!workspace) {
		return (
			<div className="flex items-center justify-center">
				<Loader />
			</div>
		)
	}

	return (
		<>
			<div className="space-y-4 pt-1">
				<PublishPortalCard
					title="Feature requests"
					description="Customers can access the requests with this link."
					link="request"
					checked={workspace.domainPublic}
					onCheckedChange={async (value) => {
						await mutateOk(
							zero.mutate(
								mutators.workspaces.update({
									id: workspace.id,
									domainPublic: value,
								}),
							),
						)
						analytics.track("toggle-roadmap-visibility", {
							value,
							popover: true,
						})
					}}
				/>

				<PortalSettingsCard dimmed={!workspace.domainPublic} />

				<MultipleCustomersCard dimmed={!workspace.domainPublic} />

				<SettingsContainer
					className={cn("transition-opacity duration-200 ease-in-out", {
						"opacity-50": !workspace.domainPublic,
					})}
				>
					<SettingItem
						title="Sorting"
						description="Organize items your way."
						action={
							<Select
								name="sort"
								className="w-44"
								value={workspace.roadmapSettings?.sortOrder ?? "SCORE_DESC"}
								onChange={async (value) => {
									await mutateOk(
										zero.mutate(
											mutators.workspaces.updateRoadmapSettings({
												workspaceId: workspace.id,
												sortOrder: value,
											}),
										),
									)
									analytics.track("update-roadmap-sort-order", { value })
								}}
								options={ROADMAP_SORT_OPTIONS.map(({ value, label, Icon }) => ({
									value,
									label: (
										<span className="flex items-center gap-2">
											<Icon className="size-4 flex-none text-tertiary" />
											{label}
										</span>
									),
								}))}
								selectClassName="dark:bg-gray-700/70"
							/>
						}
					/>

					<SettingItem
						className="pt-4"
						title="Issue grouping"
						description="For issues whose project is on the roadmap too."
						action={
							<Select
								name="issue-grouping"
								className="w-44"
								value={
									workspace.roadmapSettings?.groupIssuesUnderProject === false
										? "separate"
										: "project"
								}
								onChange={async (value) => {
									const groupIssuesUnderProject = value === "project"
									await mutateOk(
										zero.mutate(
											mutators.workspaces.updateRoadmapSettings({
												workspaceId: workspace.id,
												groupIssuesUnderProject,
											}),
										),
									)
									analytics.track("update-roadmap-issue-grouping", {
										value: groupIssuesUnderProject,
									})
								}}
								options={[
									{ value: "project", label: "Group under project" },
									{ value: "separate", label: "Show separately" },
								]}
								selectClassName="dark:bg-gray-700/70"
							/>
						}
					/>

					<SettingItem
						className="pt-4"
						title="Due dates"
						description="Show the due date of Linear issues and projects."
						action={
							<Switch
								name="due-date"
								label={null}
								checked={workspace.isDueDateEnabled}
								onCheckedChange={async (value) => {
									await mutateOk(
										zero.mutate(
											mutators.workspaces.update({
												id: workspace.id,
												isDueDateEnabled: value,
											}),
										),
									)
									analytics.track("toggle-roadmap-due-date", {
										value,
										popover: true,
									})
								}}
							/>
						}
					/>

					<SettingItem
						className="pt-4"
						title="Initiatives"
						description="Show Linear Initiatives on your portal."
						action={
							<Switch
								name="initiatives"
								label={null}
								checked={workspace.isRoadmapsEnabled}
								onCheckedChange={async (value) => {
									await mutateOk(
										zero.mutate(
											mutators.workspaces.update({
												id: workspace.id,
												isRoadmapsEnabled: value,
											}),
										),
									)
									analytics.track("toggle-roadmap-initiative", {
										value,
										popover: true,
									})
								}}
							/>
						}
					/>

					<SettingItem
						className="pt-4"
						title="Linear issue description"
						description="Display the Linear issue description on the roadmap."
						action={
							<Switch
								name="linear-issue-description"
								label={null}
								checked={workspace.roadmapSettings?.linearIssueDescription}
								onCheckedChange={async (value) => {
									await mutateOk(
										zero.mutate(
											mutators.workspaces.updateRoadmapSettings({
												workspaceId: workspace.id,
												linearIssueDescription: value,
											}),
										),
									)
									analytics.track("toggle-roadmap-linear-issue-description", {
										value,
										popover: true,
									})
								}}
							/>
						}
					/>
				</SettingsContainer>

				<SettingsContainer>
					<TeamRequestsSwitch />
				</SettingsContainer>
			</div>
		</>
	)
})
